'use client'

import { cva } from 'class-variance-authority'

import type { JSX, ReactNode } from 'react'

import useRevealOnView from './use-reveal-on-view'

/**
 * The home page's scroll reveal (`homepage-feedback-pass.css:52-64`, `app.js:1168-1203`): an 8px
 * rise and fade over 220ms `ease`, staggered 70ms by `order`, starting once 14% of the block is in
 * view and 7% above the bottom edge. Measured on production (computed styles, 2026-09-23). No motion
 * token: production's own `--sj-motion-duration` is 160ms and this reveal overrides it.
 *
 * The hidden state exists only under `reveal:` (`motion-safe` AND `scripting: enabled`), and only until
 * `data-visible` arrives. So someone who turned animation off never has content withheld, and nor
 * does anyone with JS off: `scripting` does the job of production's JS-added `.motion-ready` class.
 * The transition itself needs no gate: with no hidden state there is nothing to animate.
 */
const reveal = cva(
  'transition-[opacity,translate] duration-[220ms] ease-[ease] reveal:not-data-visible:translate-y-[8px] reveal:not-data-visible:opacity-0',
  { variants: { order: { 0: '', 1: 'delay-[70ms]', 2: 'delay-[140ms]' } }, defaultVariants: { order: 0 } },
)

type RevealProps = { children: ReactNode; className?: string; order?: 0 | 1 | 2 }

export default function Reveal({ children, className, order }: RevealProps): JSX.Element {
  const { ref, visible } = useRevealOnView<HTMLDivElement>({ threshold: 0.14, rootMargin: '0px 0px -7%' })

  return (
    <div ref={ref} data-visible={visible || undefined} className={reveal({ order, className })}>
      {children}
    </div>
  )
}
